import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, LiquidityAccount, LiquidityPosition, LiquidityPositionDetail, LiquiditySeries, Unternehmen } from "@kundeninfo/api-client";
import { KiKnopf, Suchfeld } from "../../components/meHuellen.js";
import { positionGroups, positionKey, euro } from "./liquidityModel.js";
import type { Draft, PositionGroup } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import { ANKER, LiquidityPositionObjekt, quellenSchluessel } from "./LiquidityPositionObjekt.js";
import type { PositionQuelle } from "./LiquidityPositionObjekt.js";
import type { PlanningAmount } from "./PlanningItemPanel.js";
import "./Liquidity.css";

const PI = T.planningItem;

/** Der Auslöser eines Panels: `anchor` findet ihn in der Spalte wieder (ein Wirt, dessen `focus()` seinen Knopf trifft, W-8), `key` ist seine Zeile. */
type FocusTarget = { anchor: string; key: string };
/** `stale`: Es gibt eine neue Reihe, die bekannten Posten stehen, bis die neuen da sind. `refreshing`: Der Abruf läuft. */
export type Detail = { state: "loading" } | { state: "failed" } | { state: "done" | "stale" | "refreshing"; detail: LiquidityPositionDetail };

/** Was die Spalte an Planpositionen auslöst; `useLiquidityArea` holt dafür den Stand mit Version. */
export type PlanningIntent = { kind: "new"; group: "IN" | "OUT" } | { kind: "edit"; position: LiquidityPosition } | { kind: "changeAmount"; position: LiquidityPosition }
  | { kind: "deleteAmount"; position: LiquidityPosition; amount: PlanningAmount };

/** Schalter und Einfrieren: nur bei genau einem Unternehmen mit Basis (593, P6). Sie ändern den Entwurf, nichts sonst; `locked`, solange er gespeichert wird. */
type DraftEditing = { editable: false } | { editable: true; draft: Draft; locked: boolean; onToggleEnabled: (p: LiquidityPosition) => void; onToggleFreeze: (p: LiquidityPosition) => void };

export type LiquidityPositionsProps = DraftEditing & {
  series: LiquiditySeries; accounts: readonly LiquidityAccount[]; client: Pick<ApiClient, "getLiquidityPosition">;
  scenarioId: string | null;
  /** Die neue Reihe zeigt dieselben Daten (andere Verdichtung, Vorschau, Vergleich): Die bekannten Posten gelten weiter, nichts lädt nach (Review 6, H2/H3). */
  keepPosts?: boolean;
  /** „Konto anlegen …“ (`null`) oder der Panelknopf eines Kontos. */
  onAccount: (accountId: string | null) => void;
  onBalance: (accountId: string) => void;
  /** „Erwartungstermin ändern …“ an einer offenen Rechnung. */
  onExpectedPayment: (invoiceId: string) => void;
  /** „Neue Planposition …“, Panelknopf, „Betrag ändern ab …“, Betrag löschen. */
  onPlanning: (intent: PlanningIntent) => void;
  /** Im Objektpanel des Gastgebers wurde geschrieben (F9): Die Reihe lädt im Stehen nach, der Fokus geht danach zurück wie bei `panelSaved`. */
  onGeaendert: () => void;
  /** Ein Panel aus der Spalte hat gespeichert: Ist der Fokus nach der nächsten Reihe weg, geht er zurück an den Auslöser. */
  panelSaved: boolean;
  /** Für das Kürzel an der Zeile, wenn mehrere Unternehmen in der Sicht stehen (RF2). */
  companies?: readonly Unternehmen[];
};

/** Liegt der Fokus auf dem Knopf dieses Auslösers, gibt ihn frei; Fokus anderswo bleibt, wo er ist. */
function releaseFocusOf(anchor: string): void {
  const active = document.activeElement;
  if (active === null || active.closest(anchor) === null) return;
  ((active.shadowRoot?.activeElement ?? active) as HTMLElement).blur();
}

/**
 * DIE POSITIONSSPALTE (Spec „Positionsspalte“, Plan P8): die Listenspalte der Liquidität mit Suchfeld und den Gruppen Konten, Einnahmen,
 * Ausgaben, die Summe klebt im Kopf. Gruppen, Folge und Suche rechnet `positionGroups` (Hinweis zuerst, dann Betrag absteigend).
 *
 * Der Schlüssel einer Zeile ist `companyId|ref`: Derselbe Lieferant in zwei Unternehmen sind zwei Zeilen (RF2), der Aufklapper fragt mit
 * der `companyId` seiner Zeile. Er lädt die Posten einmal; eine neue Reihe verwirft das Gemerkte, offene Zeilen laden dann neu. Jede Zeile
 * ist ein `LiquidityPositionObjekt` („Öffnen statt Springen“ Aufgabe 4): Name als Text, Verweis im Fuß (Objektpanel, eigenes Panel, Sprung).
 * Die Objekte einer Gruppe stehen als Geschwister in EINEM Behälter (W-4: gleiche Spalte vorn).
 *
 * Neu geladen wird im Stehen: Die bekannten Posten bleiben, bis die neuen da sind (Spec „Dialoge“: „bei gleicher Rollposition“). Sonst schrumpfte
 * die Spalte, und der Browser kappte die Rollposition. Eine Rechnung behält ihren Schlüssel, auch mit neuem Termin. Sie kann trotzdem wandern
 * (die Posten stehen nach Termin) oder wegfallen (Termin nach dem Horizont): Dann gibt `focusAfterReload` den Fokus an den Auslöser zurück,
 * ersatzweise an den Klapp-Pfeil der Zeile (`me-objekt.focus()`, W-8) oder ans Suchfeld, ohne zu rollen und nur, wenn er sonst auf `body` läge.
 * Den Knopf lässt sie vor dem Zeichnen los (`releaseFocusOf`): React holt den Fokus eines umgereihten Knotens sonst selbst zurück, und das rollt
 * die Spalte mit. Dasselbe gilt nach dem Schreiben im Objektpanel des Gastgebers (`onGeaendert`, Spec F9).
 *
 * Für die Planpositionen gilt dasselbe, nur wandert hier die ganze Zeile (ein neuer Betrag ändert die Folge) und schon mit der neuen Reihe.
 * Deshalb lässt die Spalte den Knopf los, sobald das Panel gespeichert hat und bevor die Reihe kommt.
 *
 * Schalter und Einfrieren (Stufe 2): Der Entwurf geht dem Stand der Reihe vor, die Zeile antwortet also sofort. Die Folge ändert sich dabei nicht, denn eine ausgeschaltete
 * Position behält ihre Beträge (`wouldBe` der Reihe); der Fokus bleibt am Schalter.
 */
export function LiquidityPositions(p: LiquidityPositionsProps): ReactElement {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [details, setDetails] = useState<ReadonlyMap<string, Detail>>(new Map());
  const generation = useRef(0);
  const column = useRef<HTMLDivElement>(null);
  const trigger = useRef<FocusTarget | null>(null);
  const focusAfterReload = useRef<FocusTarget | null>(null);
  const { series, accounts, client, scenarioId, panelSaved, keepPosts = false } = p;
  const byKey = useMemo(() => new Map(series.positions.map((x) => [positionKey(x), x])), [series]);

  /** Gibt den Fokus ohne Rollen an den Auslöser zurück, ersatzweise an den Klapp-Pfeil der Zeile oder ans Suchfeld; Fokus anderswo bleibt, wo er ist. */
  const giveFocusBack = (target: FocusTarget): void => {
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    const root = column.current;
    const next = root?.querySelector<HTMLElement>(target.anchor) ?? root?.querySelector<HTMLElement>(ANKER.objekt(target.key))
      ?? root?.querySelector<HTMLElement>(".liq__search");
    next?.focus({ preventScroll: true });
  };

  const firstSeries = useRef(series);
  useEffect(() => {
    if (firstSeries.current === series) return;
    firstSeries.current = series;
    if (keepPosts) return;
    generation.current += 1;
    if (panelSaved) focusAfterReload.current = trigger.current;
    setDetails((m) => new Map([...m].flatMap(([k, d]): [string, Detail][] => ("detail" in d ? [[k, { state: "stale", detail: d.detail }]] : []))));
  }, [series, panelSaved, keepPosts]);

  useEffect(() => {
    const target = trigger.current;
    if (target === null) return;
    if (panelSaved) releaseFocusOf(target.anchor);
    // Fällt `panelSaved` ohne wartende Rückgabe, kam die Reihe nicht: Der losgelassene Knopf bekommt den Fokus trotzdem zurück.
    else if (focusAfterReload.current === null) giveFocusBack(target);
  }, [panelSaved]);

  useEffect(() => {
    for (const key of open) {
      const position = byKey.get(key);
      const current = details.get(key);
      if (position === undefined || position.kind === "ACCOUNT" || (current !== undefined && current.state !== "stale")) continue;
      const asked = generation.current;
      const settle = (d: Detail): void => {
        if (generation.current !== asked) return;
        if (focusAfterReload.current?.key === key) releaseFocusOf(focusAfterReload.current.anchor);
        setDetails((m) => new Map(m).set(key, d));
      };
      setDetails((m) => new Map(m).set(key, current === undefined ? { state: "loading" } : { state: "refreshing", detail: current.detail }));
      client.getLiquidityPosition(position.kind, position.id,
        { ...(scenarioId === null ? {} : { scenario: scenarioId }), companyIds: [position.companyId] })
        .then((detail) => { settle({ state: "done", detail }); }, () => { settle({ state: "failed" }); });
    }
  }, [open, details, byKey, client, scenarioId]);

  // Nur auf `details`: Die neue Reihe allein entscheidet noch nichts, erst ihre Posten.
  useEffect(() => {
    const target = focusAfterReload.current;
    if (target === null) return;
    const state = details.get(target.key)?.state;
    if (byKey.has(target.key) && open.has(target.key) && state !== "done" && state !== "failed") return;
    focusAfterReload.current = null;
    giveFocusBack(target);
  }, [details]);

  const setExpanded = (key: string, expanded: boolean): void => {
    // Schließen verwirft einen Fehlschlag: Das nächste Öffnen fragt neu, sonst bliebe er bis zur nächsten Reihe stehen.
    if (!expanded && details.get(key)?.state === "failed") {
      setDetails((m) => {
        const next = new Map(m);
        next.delete(key);
        return next;
      });
    }
    setOpen((o) => {
      const next = new Set(o);
      if (expanded) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const groups = positionGroups(series.positions, search);
  const needle = search.trim().toLocaleLowerCase("de-DE");
  const withPosition = new Set(series.positions.filter((x) => x.kind === "ACCOUNT").map((x) => x.id));
  const extraAccounts = accounts.filter((a) => !withPosition.has(a.id) && a.name.toLocaleLowerCase("de-DE").includes(needle));
  const nothing = needle !== "" && extraAccounts.length === 0 && groups.every((g) => g.positions.length === 0);
  const several = series.companyIds.length > 1;

  const planning = (anchor: string, key: string, intent: PlanningIntent): void => {
    trigger.current = { anchor, key };
    p.onPlanning(intent);
  };
  /** Das Objekt der Zeile; die Anker der Aktionen bestimmen, wohin der Fokus nach dem Neuladen zurückgeht. */
  const objekt = (x: PositionQuelle): ReactElement => {
    const key = quellenSchluessel(x), position = "konto" in x ? null : x;
    const konto = "konto" in x ? x.konto : x.kind === "ACCOUNT" ? accounts.find((a) => a.id === x.id) : undefined;
    const change = p.editable && position !== null ? p.draft.get(position.ref) : undefined;
    return (
      <LiquidityPositionObjekt key={key} x={x} {...(konto === undefined ? {} : { konto })} editierbar={p.editable} gesperrt={p.editable && p.locked}
        aufgeklappt={open.has(key)} onAufgeklappt={(offen) => setExpanded(key, offen)} details={details.get(key)} aenderung={change}
        companies={p.companies ?? []} mehrereUnternehmen={several && p.companies !== undefined}
        onEinbeziehen={() => { if (p.editable && position !== null) p.onToggleEnabled(position); }}
        onEinfrieren={() => { if (p.editable && position !== null) p.onToggleFreeze(position); }}
        onPlanung={(i) => planning(i.kind === "edit" ? ANKER.verweis(key) : i.kind === "changeAmount" ? ANKER.menue(key)
          : i.kind === "deleteAmount" ? ANKER.betrag(i.amount.id) : ANKER.objekt(key), key, i)}
        onKonto={(a) => { const id = konto?.id ?? position?.id; if (id !== undefined) { if (a === "edit") p.onAccount(id); else p.onBalance(id); } }}
        onErwartungstermin={(invoiceId) => {
          trigger.current = { anchor: ANKER.rechnung(invoiceId), key };
          p.onExpectedPayment(invoiceId);
        }}
        onGeaendert={(anchor) => {
          trigger.current = { anchor, key };
          p.onGeaendert();
        }} />
    );
  };

  const addPlanningItem = (group: "IN" | "OUT"): ReactElement => (
    <span className="liq__group-action">
      <KiKnopf kompakt name={PI.addTo(T.group[group])} onClick={() => planning(`[data-group="${group}"] .liq__group-action me-knopf`, `group:${group}`, { kind: "new", group })}>{PI.add}</KiKnopf>
    </span>
  );

  const groupBlock = (g: PositionGroup): ReactElement => (
    <section key={g.key} className="liq__group" data-group={g.key}>
      <header className="liq__group-head">
        <h3 className="liq__group-title">{T.group[g.key]}</h3>
        <span className="liq__group-sum liq__amount">{euro(g.sumCents)}</span>
        {g.key !== "ACCOUNT" && addPlanningItem(g.key)}
      </header>
      {g.key === "ACCOUNT" && <KiKnopf kompakt onClick={() => p.onAccount(null)}>{T.positions.addAccount}</KiKnopf>}
      <div className="liq__objekte">
        {g.positions.map(objekt)}
        {g.key === "ACCOUNT" && extraAccounts.map((konto) => objekt({ konto }))}
      </div>
    </section>
  );

  return (
    <div className="liq__column" ref={column}>
      <Suchfeld className="liq__search" name={T.positions.search} platzhalter={T.positions.search} wert={search} onText={setSearch} />
      {nothing && <p className="liq__muted">{T.positions.empty}</p>}
      {groups.map(groupBlock)}
    </div>
  );
}
